import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Space from "../../layout/Space";

import TextField2 from "../../textfields/TextField2";
import { useSnackbar } from "notistack";
import { useAppContext } from "../../config/AppContext";
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken";
import SmallSpinner from "../../loading/SmallSpinner";

import {
  getArrayDocDataByID,
  getArrayDocDataByRef,
  updateDocDataField,
} from "../../models/DataFunctions";
import updateDocDataFieldApi from "../../api/database/updateDocDataFieldApi";

import TextField2T from "../../textfields/TextField2T";
import Gleap from "gleap";
import { SaveBar } from "../../textfields/EasySwitch";
import TabGPT from "../../tabs/TabGPT";
import sendRTBatimentTest from "../../api/send/sendRTBatimentTest";
import { DropDown, Org, RTB_RE20, User } from "../../types/basicTypes";
import { Divider, Spin } from "antd";
import PrimaryButton from "../../buttons/PrimaryButton";
import DropDownGPT03 from "../../dropdown/DropDownGTP03";
import Icon1 from "../../assets/icons/Icon";
import addSignature from "../../api/sign/addSignature";

interface RTBProps {
  userData: User;
  orgData: Org;
  navigateWorkspace: (path: string) => void;
  handleClickVariant: (
    message: string,
    variant: "default" | "error" | "success" | "warning" | "info" | undefined
  ) => void;
}

const RTB: React.FC<RTBProps> = ({
  userData,
  orgData,
  navigateWorkspace,
  handleClickVariant,
}) => {
  const { currentUser, loading } = useAuth();
  const [params] = useSearchParams();
  const section = params.get("section");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rtb, setRtb] = useState<RTB_RE20 | undefined>(undefined);
  const [activeTab, setActiveTab] = useState(section || "login");
  const tabs = [
    { label: "Identifiants", id: "login" },
    { label: "Formulaire", id: "form" },
    { label: "Signatures", id: "signature" },
  ];
  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const [loadingData, setLoadingData] = useState(false);
  const [loadingData2, setLoadingData2] = useState(false);
  const [loadingData3, setLoadingData3] = useState(false);

  const [operators, setOperators] = useState<User[]>([]);

  const handleSave = async (e: any) => {
    e.preventDefault();
    setLoadingData2(true);
    await updateDocDataFieldApi(
      orgData.ref!,
      "config.atf.rtb_re2020",
      rtb,
      prefix
    );
    handleClickVariant("Enregistré", "success");
    setLoadingData2(false);
  };

  const handleSaveBat = async (e: any) => {
    e.preventDefault();
    setLoadingData3(true);
    await updateDocDataFieldApi(
      orgData.ref!,
      "config.atf.rtb_re2020",
      rtb,
      prefix
    );
    handleClickVariant("Enregistré", "success");
    setLoadingData3(false);
  };

  const handleConnect = async (e: any) => {
    e.preventDefault();
    setLoadingData(true);
    const token = await getOrRefreshFirebaseToken();
    const success = await sendRTBatimentTest(username, password, token, prefix);
    if (success && rtb) {
      handleClickVariant("Connexion réussie", "success");
      rtb.username = username;
      rtb.password = password;
      rtb.connected = true;
      await updateDocDataFieldApi(
        orgData.ref!,
        "config.atf.rtb_re2020",
        rtb,
        prefix
      );
      handleFetchData();
    } else {
      handleClickVariant("Connexion échouée", "error");
      await updateDocDataFieldApi(
        orgData.ref!,
        "config.atf.rtb_re2020.connected",
        false,
        prefix
      );
      handleFetchData();
    }
    setLoadingData(false);
  };

  // Fetch user, org, clients, and article data
  const handleFetchData = async () => {
    if (userData) {
      try {
        const rtb = orgData.config?.atf?.rtb_re2020;
        if (rtb) {
          const username = rtb.username || "";
          const password = rtb.password || "";
          setUsername(username);
          setPassword(password);
          setRtb(rtb);
        } else {
          let nw_rtb = initializeRTB();
          await updateDocDataFieldApi(
            orgData.ref!,
            "config.atf.rtb_re2020",
            nw_rtb,
            prefix
          );
          setRtb(nw_rtb);
        }
        const users = await getArrayDocDataByID("user", orgData.users_id);

        setOperators(users);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const handleAddSignature = async (uid: string) => {
    const token = await getOrRefreshFirebaseToken();
    //await sendOperatorSignature(uid, orgData.id, token, prefix);
    let added = await addSignature(token, prefix);
    if (added) {
      handleClickVariant("Signature ajoutée", "success");
    } else {
      handleClickVariant("Erreur lors de l'ajout de la signature", "error");
    }
    handleFetchData();
  };

  useEffect(() => {
    if (orgData) {
      handleFetchData();
    }
  }, [orgData]);

  if (loading || !currentUser || !orgData || !rtb) {
    return (
      <div className="w-full h-full overflow-auto flex flex-row gap-2 mt-3">
        <Spin></Spin>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full pl-4  overflow-y-auto">
      <div className="flex flex-col h-fit pb-40 w-c500">
        <div className="text-h1 font-bold text-left">RT Batiment</div>
        <Space y={20} />
        <TabGPT tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />

        <Space y={5} />
        {activeTab === "login" && (
          <>
            {" "}
            <Space y={30} />
            <div className="text-h1 text-black font-medium">
              Connectez-vous à RT Batiment
            </div>
            <Space y={10} />
            <div className="text-h3 text-shadow_50">
              Bienvenue dans l'intégration RT Batiment. Ici, vous pouvez
              configurer la génération automatique d'attestation RE2020.
            </div>
            <div className="text-h3 text-shadow_50">
              Ajoutez les identifiants de votre compte RT Batiment pour activer
              l'intégration.
            </div>
            <Space y={10} />
            <form onSubmit={handleConnect} className="w-full">
              <div className="w-full flex flex-col">
                <TextField2T
                  label="Identifiant"
                  autoComplete={"username"}
                  value={username}
                  onChange={(e: any) => setUsername(e.target.value)}
                />
                <Space y={10} />
                <TextField2T
                  label="Mot de passe"
                  type="password"
                  autoComplete={"password"}
                  value={password}
                  can_display={false}
                  onChange={(e: any) => setPassword(e.target.value)}
                />

                <Space y={10} />
                <div className="flex flex-row gap-2">
                  <div
                    className={` rounded-xl py-2 text-shadow_50 ${
                      rtb.connected
                        ? "  border-2 border-green-400"
                        : " border-2 border-red-400"
                    }  px-2 text-h2 font-semibold w-fit h-fit`}
                  >
                    {rtb.connected ? "Connecté" : "Déconnecté"}
                  </div>
                  <PrimaryButton
                    className="w-fit"
                    type="submit"
                    disabled={username === "" || password === ""}
                    onClick={handleConnect}
                  >
                    <div className="flex flex-row content-center align-middle items-center">
                      Connecter <Space x={5} />
                      <SmallSpinner
                        loading={loadingData}
                        margin={0}
                        size={20}
                      />
                    </div>
                  </PrimaryButton>
                </div>
              </div>
            </form>
          </>
        )}

        {activeTab === "form" && (
          <>
            <Space y={30} />
            <div className="text-h1 text-black font-medium">
              Formulaire RE 2020
            </div>

            <Space y={10} />
            <div className="text-h3 text-shadow_50">
              Remplissez le formulaire pour configurer les champs par défaut de
              vos attestations RE 2020.
            </div>
            <Space y={30} />
            <div className="text-h3 text-shadow_50 font-semibold">
              Champs informations
            </div>
            <Space y={20} />
            <div className="flex flex-col gap-1 w-c500">
              <TextField2T
                mandatory={true}
                label={"Nom auteur"}
                value={rtb.operation_fc_form_auteur_nom}
                onChange={(e: any) =>
                  setRtb({
                    ...rtb,
                    operation_fc_form_auteur_nom: e.target.value,
                  })
                }
              ></TextField2T>
              <TextField2T
                mandatory={true}
                label={"Nom entreprise"}
                value={rtb.operation_fc_form_auteur_societe}
                onChange={(e: any) =>
                  setRtb({
                    ...rtb,
                    operation_fc_form_auteur_societe: e.target.value,
                  })
                }
              ></TextField2T>
              <TextField2T
                mandatory={true}
                label={"Code postal"}
                value={rtb.operation_fc_form_auteur_cp}
                onChange={(e: any) =>
                  setRtb({
                    ...rtb,
                    operation_fc_form_auteur_cp: e.target.value,
                  })
                }
              ></TextField2T>
              <TextField2T
                mandatory={true}
                label={"Localité"}
                value={rtb.operation_fc_form_auteur_localite}
                onChange={(e: any) =>
                  setRtb({
                    ...rtb,
                    operation_fc_form_auteur_localite: e.target.value,
                  })
                }
              ></TextField2T>
              <TextField2T
                mandatory={true}
                label={"Adresse"}
                value={rtb.operation_fc_form_auteur_adresse1}
                onChange={(e: any) =>
                  setRtb({
                    ...rtb,
                    operation_fc_form_auteur_adresse1: e.target.value,
                  })
                }
              ></TextField2T>
              <div className="flex flex-col gap-2 ">
                <div className="flex flex-row gap-4 ">
                  <DropDownGPT03
                    mandatory={true}
                    label={"En qualité de"}
                    items={[
                      {
                        value: "1",
                        label: "Organisme de contrôle technique",
                      },
                      {
                        value: "2",
                        label: "Architecte",
                      },
                      {
                        value: "3",
                        label: "Diagnostiqueur de performance énergétique",
                      },
                      {
                        value: "4",
                        label:
                          "Organisme ayant certifié la performance énergétique d’un nouveau bâtiment et ayant signé une convention avec le ministre chargé de la construction",
                      },
                      {
                        value: "5",
                        label: "Bureau d'étude agréé",
                      },
                    ]}
                    value={rtb.operation_fc_form_auteur_qualite?.value}
                    onChange={(value) =>
                      setRtb({
                        ...rtb,
                        operation_fc_form_auteur_qualite: {
                          ...rtb.operation_fc_form_auteur_qualite,
                          value: value.value.toString(),
                        },
                      })
                    }
                    className="w-c500"
                  />{" "}
                </div>
              </div>

              <SaveBar
                condition={rtb !== orgData.config?.atf?.rtb_re2020}
                onClick={() => handleSave({ preventDefault: () => {} })}
              />

              <Space y={10} />
            </div>
            <Space y={30} />
            <div className="text-h3 text-shadow_50 font-semibold">
              Champs batiment
            </div>
            <Space y={20} />
            <div className="flex flex-col gap-2 w-fit">
              <DropForm
                mandatory={true}
                label={
                  "Fourniture du récapitulatif standardisé d'étude énergétique"
                }
                value={rtb.operation_fc_form_xml_depose.value}
                onChange={(value: string) => {
                  console.log("Value changed:", value);

                  setNewRTB(
                    value,
                    rtb,
                    "operation_fc_form_xml_depose",
                    rtb.operation_fc_form_xml_depose,
                    setRtb
                  );
                }}
              />

              <DropForm
                label={
                  "Le bâtiment est-il situé dans un périmètre de développement prioritaire d'un réseau de chaleur ou de froid classé ?"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_perimetre_reseau_chaleur.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_perimetre_reseau_chaleur",
                    rtb.batiment_fc_form_perimetre_reseau_chaleur,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Une dérogation à l’obligation de raccordement a-t-elle été obtenue ?"
                }
                mandatory={true}
                value={
                  rtb.batiment_fc_form_perimetre_reseau_chaleur_derogation.value
                }
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_perimetre_reseau_chaleur_derogation",
                    rtb.batiment_fc_form_perimetre_reseau_chaleur_derogation,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Pour les générateurs de chaleur ou de froid utilisés pour le chauffage, la production d'eau chaude sanitaire et/ou le refroidissement des locaux la cohérence entre le récapitulatif standardisé d'étude énergétique et environnementale et le contrôle visuel sur site du nombre et du type de générateur"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_cep_nr_coherence.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_cep_nr_coherence",
                    rtb.batiment_fc_form_cep_nr_coherence,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Fourniture de documents justifiant des quantitatifs et des références des produits renseignés dans le récapitulatif standardisé d'étude énergétique et environnementale par le maître d'ouvrage"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_ic_construction_documents.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_ic_construction_documents",
                    rtb.batiment_fc_form_ic_construction_documents,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Pour les données d'entrée du calcul des indicateurs d'impact sur le changement climatique du bâtiment sélectionnées, les documents justifiant des quantitatifs et des références des produits renseignés dans le récapitulatif standardisé d'étude énergétique et environnementale et le récapitulatif standardisé d'étude énergétique et environnementale sont cohérents"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_ic_construction_coherence1.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_ic_construction_coherence1",
                    rtb.batiment_fc_form_ic_construction_coherence1,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Pour les données d'entrée du calcul des indicateurs d'impact sur le changement climatique du bâtiment sélectionnées, les documents justifiant des quantitatifs et des références des produits renseignés dans le récapitulatif standardisé d'étude énergétique et environnementale et le contrôle visuel sur site sont cohérents"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_ic_construction_coherence2.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_ic_construction_coherence2",
                    rtb.batiment_fc_form_ic_construction_coherence2,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Fourniture de documents de justification des isolants posés par le maître d'ouvrage"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_isolants_fourniture_documents.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_isolants_fourniture_documents",
                    rtb.batiment_fc_form_isolants_fourniture_documents,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Pour l'ensemble des isolants, cohérence entre le récapitulatif standardisé d'étude énergétique et environnementale et le document de justification des isolants posés par le maître d'ouvrage"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_isolants_coherence.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_isolants_coherence",
                    rtb.batiment_fc_form_isolants_coherence,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Pour l'ensemble des isolants, respect de l'exigence U ≤ 0,36 W/(m².K)"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_isolants_respect_exigence.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_isolants_respect_exigence",
                    rtb.batiment_fc_form_isolants_respect_exigence,
                    setRtb
                  )
                }
              />
              <DropForm
                label={"Présence de protections solaires"}
                mandatory={true}
                value={rtb.batiment_fc_form_protections_solaires_presence.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_protections_solaires_presence",
                    rtb.batiment_fc_form_protections_solaires_presence,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Cohérence entre le récapitulatif standardisé d'étude énergétique et environnementale et le contrôle visuel sur site"
                }
                mandatory={true}
                value={
                  rtb.batiment_fc_form_protections_solaires_coherence.value
                }
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_protections_solaires_coherence",
                    rtb.batiment_fc_form_protections_solaires_coherence,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "À l'exception des baies des locaux à occupation passagère, les baies ont un facteur solaire inférieur ou égal au facteur solaire défini dans le tableau de l'article 24 de l'arrêté du 4 août 2021"
                }
                mandatory={true}
                value={
                  rtb.batiment_fc_form_protections_solaires_respect_exigence
                    .value
                }
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_protections_solaires_respect_exigence",
                    rtb.batiment_fc_form_protections_solaires_respect_exigence,
                    setRtb
                  )
                }
              />
              <DropForm
                label={"Le projet est un IGH ou la Sref du projet > 3 000 m² ?"}
                mandatory={true}
                value={rtb.batiment_fc_form_permeabilite_air_igh.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_permeabilite_air_igh",
                    rtb.batiment_fc_form_permeabilite_air_igh,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Document de justification transmis par le maître d'ouvrage"
                }
                mandatory={true}
                items={[
                  { value: "1", label: "Mesure sur site" },
                  { value: "2", label: "Démarche qualité" },
                ]}
                value={
                  rtb.batiment_fc_form_permeabilite_air_document_justification
                    .value
                }
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_permeabilite_air_document_justification",
                    rtb.batiment_fc_form_permeabilite_air_document_justification,
                    setRtb
                  )
                }
              />
              <div className="text-h3 text-black">Cas Mesure sur site</div>

              <>
                {" "}
                <DropForm
                  label={"Fourniture du rapport de mesure"}
                  mandatory={true}
                  value={
                    rtb.batiment_fc_form_permeabilite_air_fourniture_rapport
                      .value
                  }
                  onChange={(value: any) =>
                    setNewRTB(
                      value,
                      rtb,
                      "batiment_fc_form_permeabilite_air_fourniture_rapport",
                      rtb.batiment_fc_form_permeabilite_air_fourniture_rapport,
                      setRtb
                    )
                  }
                />
                <DropForm
                  label={
                    "Le mesureur qui a signé le rapport de mesure figure sur la liste des mesureurs autorisés par le ministère chargé de la construction"
                  }
                  mandatory={true}
                  value={
                    rtb.batiment_fc_form_permeabilite_air_mesureur_autorise
                      .value
                  }
                  onChange={(value: any) =>
                    setNewRTB(
                      value,
                      rtb,
                      "batiment_fc_form_permeabilite_air_mesureur_autorise",
                      rtb.batiment_fc_form_permeabilite_air_mesureur_autorise,
                      setRtb
                    )
                  }
                />
              </>
              <div className="text-h3 text-black">Cas Démarche qualité</div>

              <>
                {" "}
                <DropForm
                  label={
                    "Fourniture des documents certifiant la démarche qualité du maître d'ouvrage"
                  }
                  mandatory={true}
                  value={
                    rtb.batiment_fc_form_permeabilite_air_qualite_documents
                      .value
                  }
                  onChange={(value: any) =>
                    setNewRTB(
                      value,
                      rtb,
                      "batiment_fc_form_permeabilite_air_qualite_documents",
                      rtb.batiment_fc_form_permeabilite_air_qualite_documents,
                      setRtb
                    )
                  }
                />
                <DropForm
                  label={
                    "Le certificateur qui a signé les documents la démarche qualité du maître d'ouvrage figure sur la liste des certificateurs autorisés par le ministère chargé de la construction"
                  }
                  mandatory={true}
                  value={
                    rtb.batiment_fc_form_permeabilite_air_qualite_certificateur
                      .value
                  }
                  onChange={(value: any) =>
                    setNewRTB(
                      value,
                      rtb,
                      "batiment_fc_form_permeabilite_air_qualite_certificateur",
                      rtb.batiment_fc_form_permeabilite_air_qualite_certificateur,
                      setRtb
                    )
                  }
                />
              </>

              <DropForm
                label={
                  "Cohérence entre le récapitulatif standardisé d'étude énergétique et environnementale et les documents justificatifs de l'exigence sur la perméabilité à l'air du bâtiment"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_permeabilite_air_coherence.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_permeabilite_air_coherence",
                    rtb.batiment_fc_form_permeabilite_air_coherence,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Cohérence entre le récapitulatif standardisé d'étude énergétique et environnementale et le contrôle visuel sur site des systèmes de ventilation installés"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_ventilation_coherence.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_ventilation_coherence",
                    rtb.batiment_fc_form_ventilation_coherence,
                    setRtb
                  )
                }
              />
              <DropForm
                label={
                  "Un opérateur reconnu compétent par le ministre chargé de la construction a vérifié la conformité des systèmes de ventilation"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_ventilation_operateur.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_ventilation_operateur",
                    rtb.batiment_fc_form_ventilation_operateur,
                    setRtb
                  )
                }
                items={[
                  { value: "1", label: "Oui" },
                  { value: "0", label: "Non" },
                  { value: "2", label: "Non concerné*" },
                ]}
              />
              <DropForm
                label={
                  "Conformité du système de ventilation attestée par le rapport de vérifications et de mesure fourni par le maître d'ouvrage"
                }
                mandatory={true}
                value={rtb.batiment_fc_form_ventilation_conformite.value}
                onChange={(value: any) =>
                  setNewRTB(
                    value,
                    rtb,
                    "batiment_fc_form_ventilation_conformite",
                    rtb.batiment_fc_form_ventilation_conformite,
                    setRtb
                  )
                }
              />
            </div>
          </>
        )}

        {activeTab === "signature" && (
          <>
            <Space y={30} />
            <div className="text-h1 text-black font-medium">
              Signatures opérateurs
            </div>

            <Space y={20} />
            <div className="flex flex-col gap-2 w-c500">
              {operators.length === 0 && (
                <div className="text-shadow_50 text-h3">
                  Aucun opérateur trouvé.
                </div>
              )}
              {operators.map((operator) => (
                <div
                  key={operator.id}
                  className="flex flex-row justify-between h-c100 border-1 border-shadow_10 rounded-xl p-2"
                >
                  <div className="flex flex-col  h-c100 ">
                    <div className="text-shadow_50 text-h3 w-c200">
                      {operator.display_name}
                    </div>
                    <div className="text-shadow_50 text-h3 w-c200">
                      {operator.email}
                    </div>
                  </div>{" "}
                  <div
                    className="border-2 border-shadow_50 h-c80 w-c200 hover:shadow-lg hover:animate-tiny-bounce transition-all flex rounded-xl p-2 cursor-pointer"
                    onClick={() => handleAddSignature(operator.id)}
                    style={{
                      border: operator.sign_url
                        ? "transparent"
                        : "2px dashed #ec1a52",
                      backgroundColor: operator.sign_url
                        ? "transparent"
                        : "#ffedf2",
                    }}
                  >
                    {!operator.sign_url && (
                      <div className=" h-full w-full justify-center  items-center flex flex-col gap-1">
                        <Icon1 icon_name="image" fill="#ec1a52"></Icon1>
                        <div className="text-h3 text-red-500">
                          Ajouter une signature
                        </div>
                      </div>
                    )}
                    {operator.sign_url && (
                      <img
                        src={operator.sign_url}
                        className="object-fill  h-c80 w-c200 rounded-xl"
                      ></img>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RTB;

interface DropFormProps {
  label: string;
  value: string;
  mandatory?: boolean;
  onChange: (value: string) => void;
  items?: { value: string; label: string }[];
}

const DropForm: React.FC<DropFormProps> = ({
  label,
  value,
  mandatory = false,
  onChange,
  items = [
    { value: "1", label: "Oui" },
    { value: "0", label: "Non" },
  ],
}) => {
  return (
    <div className="flex flex-col gap-2 ">
      <div className="flex flex-row gap-2 ">
        <div className="text-shadow_50 text-h3 w-c700">{label}</div>
        <DropDownGPT03
          label={"Choisir"}
          mandatory={mandatory}
          items={items}
          value={value}
          onChange={(item) => {
            console.log("Selected item:", item);
            onChange(item as any as string);
          }}
          className="w-c200"
        />{" "}
      </div>
    </div>
  );
};

export const setNewRTB = async (
  value: string,
  rtb: RTB_RE20,
  field: string,
  old_field: DropDown,
  setRTB: (rtb: RTB_RE20) => void
) => {
  const newRtb = {
    ...rtb,
    [field]: {
      ...old_field,
      value: value,
    },
  };
  setRTB(newRtb);
};

export const initializeRTB = (): RTB_RE20 => {
  const rtb: RTB_RE20 = {
    connected: false,
    username: "",
    password: "",
    operation_fc_form_auteur_nom: "",
    operation_fc_form_auteur_societe: "",
    operation_fc_form_auteur_cp: "",
    operation_fc_form_auteur_localite: "",
    operation_fc_form_auteur_adresse1: "",
    operation_fc_form_auteur_qualite: {
      value: "1",
      label: "Organisme de contrôle technique",
    },
    operation_fc_form_date_visite: "",
    operation_fc_form_xml_depose: { value: "1", label: "Oui" },
    batiment_fc_form_perimetre_reseau_chaleur: { value: "1", label: "Oui" },
    batiment_fc_form_perimetre_reseau_chaleur_derogation: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_cep_nr_coherence: { value: "1", label: "Oui" },
    batiment_fc_form_ic_construction_documents: { value: "1", label: "Oui" },
    batiment_fc_form_ic_construction_coherence1: { value: "1", label: "Oui" },
    batiment_fc_form_ic_construction_coherence2: { value: "1", label: "Oui" },
    batiment_fc_form_isolants_fourniture_documents: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_isolants_coherence: { value: "1", label: "Oui" },
    batiment_fc_form_isolants_respect_exigence: { value: "1", label: "Oui" },
    batiment_fc_form_protections_solaires_presence: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_protections_solaires_coherence: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_protections_solaires_respect_exigence: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_permeabilite_air_igh: { value: "1", label: "Oui" },
    batiment_fc_form_permeabilite_air_document_justification: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_permeabilite_air_fourniture_rapport: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_permeabilite_air_mesureur_autorise: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_permeabilite_air_qualite_documents: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_permeabilite_air_qualite_certificateur: {
      value: "1",
      label: "Oui",
    },
    batiment_fc_form_permeabilite_air_coherence: { value: "1", label: "Oui" },
    batiment_fc_form_ventilation_coherence: { value: "1", label: "Oui" },
    batiment_fc_form_ventilation_operateur: { value: "1", label: "Oui" },
    batiment_fc_form_ventilation_conformite: { value: "1", label: "Oui" },
  };
  return rtb;
};
